import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2,
  Copy,
  Loader2,
  Link as LinkIcon,
  QrCode,
  CreditCard,
  MessageSquare,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { InstallmentStatusBadge } from "./InstallmentStatusBadge";
import {
  usePaymentPlanByBudget,
  useCreatePaymentPlan,
  useMarkInstallment,
  useCreateMpPreference,
  useCreateMpPix,
  type PaymentMode,
  type PaymentInstallment,
} from "@/hooks/usePayments";
import { format } from "date-fns";
import { formatBRL } from "@/lib/format";

const fmt = (v: number) => formatBRL(v);

interface Props {
  budgetId: string;
  total: number;
  clientId?: string | null;
  /** Enables "Enviar por WhatsApp" on each installment's public link. */
  leadPhone?: string | null;
  leadName?: string | null;
  /**
   * Plan can be defined, but nothing can be charged or marked paid yet — the
   * deal (contract or proposal) is not signed. Hides MP links, public link
   * and "Marcar pago" (flows/pagamento.md).
   */
  collectLocked?: boolean;
}

export function BudgetPaymentPlan({
  budgetId,
  total,
  clientId,
  leadPhone,
  leadName,
  collectLocked = false,
}: Props) {
  const { data, isLoading } = usePaymentPlanByBudget(budgetId);
  const create = useCreatePaymentPlan();
  const mark = useMarkInstallment();
  const mpPref = useCreateMpPreference();
  const mpPix = useCreateMpPix();

  const [mode, setMode] = useState<PaymentMode>("single");
  const [installments, setInstallments] = useState(3);
  const [depositPct, setDepositPct] = useState(50);
  const [firstDate, setFirstDate] = useState(new Date().toISOString().slice(0, 10));
  const [interval, setInterval] = useState(30);
  const [cardMax, setCardMax] = useState<Record<string, number>>({});
  const [pixModal, setPixModal] = useState<{ qr: string; b64: string } | null>(null);
  const [confirmRecreate, setConfirmRecreate] = useState(false);
  const [confirmPaid, setConfirmPaid] = useState<PaymentInstallment | null>(null);

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );

  const existing = data?.installments ?? [];
  const hasPaid = existing.some((i) => i.status === "paid");
  const paidCount = existing.filter((i) => i.status === "paid").length;

  const createPlan = () =>
    create.mutate({
      budget_id: budgetId,
      client_id: clientId ?? null,
      total_amount: total,
      mode,
      installments_count: installments,
      deposit_percent: depositPct,
      first_due_date: firstDate,
      interval_days: interval,
    });

  const payUrl = (token: string) => `${window.location.origin}/pay/${token}`;

  const copyLink = (token: string) => {
    navigator.clipboard.writeText(payUrl(token));
    toast.success("Link de pagamento copiado");
  };

  const shareWhatsApp = (i: PaymentInstallment) => {
    if (!leadPhone || !i.public_token) return;
    const phone = leadPhone.replace(/\D/g, "");
    const first = (leadName ?? "").split(" ")[0];
    const text = [
      `Olá${first ? ` ${first}` : ""}! Segue o link para pagamento de *${i.label}* (${fmt(i.amount)})${i.due_date ? `, vencimento ${format(new Date(i.due_date), "dd/MM/yyyy")}` : ""}:`,
      payUrl(i.public_token),
      "",
      "Você pode pagar por PIX ou cartão. Qualquer dúvida é só me chamar!",
    ].join("\n");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Plano de pagamento</CardTitle>
          {!data && (
            <p className="text-xs text-muted-foreground">
              Nenhum plano ainda. Escolha como o cliente vai pagar e gere as parcelas.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {hasPaid ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
              <Lock className="h-4 w-4 shrink-0" />
              {paidCount} de {existing.length} parcela(s) já paga(s) — o plano está travado para não
              apagar o histórico de pagamentos.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div>
                  <Label htmlFor="plan-modo">Modo</Label>
                  <Select value={mode} onValueChange={(v: PaymentMode) => setMode(v)}>
                    <SelectTrigger id="plan-modo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">À vista</SelectItem>
                      <SelectItem value="deposit_balance">Entrada + saldo</SelectItem>
                      <SelectItem value="installments">Parcelado (várias cobranças)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {mode === "installments" && (
                  <div>
                    <Label htmlFor="plan-parcelas">Parcelas</Label>
                    <Input
                      id="plan-parcelas"
                      type="number"
                      min={2}
                      max={24}
                      value={installments}
                      onChange={(e) => setInstallments(+e.target.value)}
                    />
                  </div>
                )}
                {mode === "deposit_balance" && (
                  <div>
                    <Label htmlFor="plan-entrada">Entrada (%)</Label>
                    <Input
                      id="plan-entrada"
                      type="number"
                      min={10}
                      max={90}
                      value={depositPct}
                      onChange={(e) => setDepositPct(+e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="plan-1-vencimento">1º vencimento</Label>
                  <Input
                    id="plan-1-vencimento"
                    type="date"
                    value={firstDate}
                    onChange={(e) => setFirstDate(e.target.value)}
                  />
                </div>
                {mode !== "single" && (
                  <div>
                    <Label htmlFor="plan-intervalo-dias">Intervalo (dias)</Label>
                    <Input
                      id="plan-intervalo-dias"
                      type="number"
                      min={1}
                      value={interval}
                      onChange={(e) => setInterval(+e.target.value)}
                    />
                  </div>
                )}
              </div>
              <Button
                onClick={() => (data ? setConfirmRecreate(true) : createPlan())}
                disabled={!total || create.isPending}
              >
                {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {data ? "Recriar plano" : "Gerar plano"} — total {fmt(total)}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {existing.length ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Parcelas</CardTitle>
            <p className="text-xs text-muted-foreground">
              Gere o PIX ou o link de cartão, depois envie o link público ao cliente. Marque como
              pago apenas para pagamentos fora da plataforma.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {existing.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center gap-2 rounded border p-3">
                  <span className="w-8 font-mono text-xs text-muted-foreground">#{i.sequence}</span>
                  <div className="min-w-[180px] flex-1">
                    <p className="text-sm font-medium">{i.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {i.due_date
                        ? `Vence em ${format(new Date(i.due_date), "dd/MM/yyyy")}`
                        : "Sem data"}
                      {i.mp_status && ` · MP: ${i.mp_status}`}
                    </p>
                  </div>
                  <span className="font-semibold">{fmt(i.amount)}</span>
                  <InstallmentStatusBadge status={i.status} shape="pill" />

                  {!collectLocked && i.status !== "paid" && (
                    <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                      <div className="flex items-center gap-1.5">
                        <Label
                          htmlFor={`cardmax-${i.id}`}
                          className="whitespace-nowrap text-[11px] text-muted-foreground"
                        >
                          Máx. parcelas no cartão
                        </Label>
                        <Input
                          id={`cardmax-${i.id}`}
                          type="number"
                          min={1}
                          max={12}
                          className="h-8 w-16"
                          value={cardMax[i.id] ?? 12}
                          onChange={(e) => setCardMax((m) => ({ ...m, [i.id]: +e.target.value }))}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            mpPref.mutate({
                              installment_id: i.id,
                              max_installments: cardMax[i.id] ?? 12,
                            })
                          }
                          disabled={mpPref.isPending}
                          title="Gera o link de pagamento com cartão no Mercado Pago"
                        >
                          <CreditCard className="mr-1 h-3.5 w-3.5" />
                          Cartão
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          mpPix.mutate(
                            { installment_id: i.id },
                            {
                              onSuccess: (r) =>
                                setPixModal({ qr: r.qr_code, b64: r.qr_code_base64 }),
                            },
                          )
                        }
                        disabled={mpPix.isPending}
                        title="Gera o QR Code PIX no Mercado Pago"
                      >
                        <QrCode className="mr-1 h-3.5 w-3.5" />
                        PIX
                      </Button>
                    </div>
                  )}

                  {!collectLocked && i.public_token && (
                    <div className="flex w-full items-center gap-1.5 sm:w-auto">
                      <Button size="sm" variant="ghost" onClick={() => copyLink(i.public_token!)}>
                        <LinkIcon className="mr-1 h-3.5 w-3.5" />
                        Copiar link
                      </Button>
                      {leadPhone && i.status !== "paid" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => shareWhatsApp(i)}
                          title="Abre o WhatsApp com o link de pagamento"
                        >
                          <MessageSquare className="mr-1 h-3.5 w-3.5" />
                          WhatsApp
                        </Button>
                      )}
                    </div>
                  )}
                  {!collectLocked && i.status !== "paid" && (
                    <Button size="sm" variant="ghost" onClick={() => setConfirmPaid(i)}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      Marcar pago
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={!!pixModal} onOpenChange={(o) => !o && setPixModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PIX gerado</DialogTitle>
          </DialogHeader>
          {pixModal?.b64 && (
            <img
              src={`data:image/png;base64,${pixModal.b64}`}
              alt="QR Code PIX"
              className="mx-auto h-64 w-64"
            />
          )}
          <Button
            variant="outline"
            onClick={() => {
              if (pixModal?.qr) {
                navigator.clipboard.writeText(pixModal.qr);
                toast.success("Código PIX copiado");
              }
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copiar copia-e-cola
          </Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmRecreate} onOpenChange={setConfirmRecreate}>
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Recriar o plano de pagamento?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              As {existing.length} parcela(s) atuais serão apagadas, junto com os links de pagamento
              e QR Codes já enviados ao cliente. Novos links precisarão ser enviados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => {
                setConfirmRecreate(false);
                createPlan();
              }}
            >
              Recriar plano
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmPaid}
        onOpenChange={(o) => {
          if (!o) setConfirmPaid(null);
        }}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Marcar "{confirmPaid?.label}" como paga?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              {confirmPaid ? fmt(confirmPaid.amount) : ""} será lançado como recebido hoje no
              financeiro. Use apenas para pagamentos feitos fora do Mercado Pago (transferência,
              dinheiro).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => {
                if (confirmPaid)
                  mark.mutate(
                    { id: confirmPaid.id, status: "paid" },
                    { onSuccess: () => toast.success("Parcela marcada como paga") },
                  );
                setConfirmPaid(null);
              }}
            >
              Confirmar pagamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
